import React, { useState } from 'react'
import { Aircraft } from '../../types/intellijetnet'
import { ContainerType } from '../../types/database'
import { MovementValidationResult } from '../../utils/aircraft-movement-validation'

interface AircraftMovementModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (reason?: string, notes?: string) => void
  aircraft: Aircraft | null
  fromContainer: ContainerType | null
  toContainer: ContainerType | null
  validation: MovementValidationResult | null
  isLoading?: boolean
}

const AircraftMovementModal: React.FC<AircraftMovementModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  aircraft,
  fromContainer,
  toContainer,
  validation,
  isLoading = false
}) => {
  const [reason, setReason] = useState('')
  const [notes, setNotes] = useState('')

  if (!isOpen || !aircraft || !fromContainer || !toContainer || !validation) {
    return null
  }

  const getContainerDisplayName = (container: ContainerType): string => {
    switch (container) {
      case 'for_sale': return 'For Sale'
      case 'sold': return 'Sold'
      case 'removed_from_market': return 'Removed from Market'
      default: return container
    }
  }

  const handleConfirm = () => {
    onConfirm(reason || undefined, notes || undefined)
  }

  const handleClose = () => {
    setReason('')
    setNotes('')
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Move Aircraft
            </h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={isLoading}
            >
              <span className="sr-only">Close</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Aircraft Info */}
          <div className="mb-4 p-3 bg-gray-50 rounded-md">
            <div className="text-sm font-medium text-gray-900">
              {aircraft.make} {aircraft.model}
            </div>
            <div className="text-sm text-gray-600">
              S/N: {aircraft.sernbr} | Reg: {aircraft.regnbr}
            </div>
          </div>

          {/* Movement Details */}
          <div className="mb-4">
            <div className="text-sm text-gray-700">
              <span className="font-medium">From:</span> {getContainerDisplayName(fromContainer)}
            </div>
            <div className="text-sm text-gray-700">
              <span className="font-medium">To:</span> {getContainerDisplayName(toContainer)}
            </div>
          </div>

          {/* Validation Results */}
          {!validation.isValid && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    Movement Not Allowed
                  </h3>
                  <div className="mt-2 text-sm text-red-700">
                    {validation.reason}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Warnings */}
          {validation.isValid && validation.warnings && validation.warnings.length > 0 && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    Warnings
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <ul className="list-disc list-inside space-y-1">
                      {validation.warnings.map((warning, index) => (
                        <li key={index}>{warning}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Confirmation Message */}
          {validation.isValid && validation.confirmationMessage && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <div className="text-sm text-blue-700">
                {validation.confirmationMessage}
              </div>
            </div>
          )}

          {/* Form Fields */}
          {validation.isValid && (
            <div className="space-y-4 mb-4">
              <div>
                <label htmlFor="reason" className="block text-sm font-medium text-gray-700">
                  Reason (Optional)
                </label>
                <input
                  type="text"
                  id="reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="e.g., Customer request, data correction"
                  disabled={isLoading}
                />
              </div>

              <div>
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                  Notes (Optional)
                </label>
                <textarea
                  id="notes"
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Additional notes about this movement"
                  disabled={isLoading}
                />
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              disabled={isLoading}
            >
              Cancel
            </button>
            {validation.isValid && (
              <button
                type="button"
                onClick={handleConfirm}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Moving...
                  </>
                ) : (
                  'Confirm Move'
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AircraftMovementModal
